/* Owns the React view of `lib/ws.ts`'s connection slice: the chip the
   StatusStrip renders (`server.wsOpen` / `server.wsReconnecting` /
   `server.wsClosed`) and the manual `server.reconnect` action. Kept out of
   `lib/` so the transport stays free of React (PANEL.md §14). */

import { useCallback, useSyncExternalStore } from 'react'

import { wsClient, type ConnectionState, type WsPublicState } from '../lib/ws.js'

function useWsState(): WsPublicState {
  return useSyncExternalStore(wsClient.subscribeState, wsClient.getState, wsClient.getState)
}

export function useConnectionState(): ConnectionState {
  return useWsState().connection
}

export function useSubscriptions(): ReadonlySet<string> {
  return useWsState().subscriptions
}

export interface ConnectionChip {
  connection: ConnectionState
  /** i18n key for the chip label — the three PANEL.md §4 states, `connecting` reading as reconnecting. */
  labelKey: string
  tone: 'ok' | 'warn' | 'danger'
  /** Only a dead socket offers the manual action; a backing-off one is already retrying. */
  canReconnect: boolean
  reconnect: () => void
}

const LABEL_KEY: Record<ConnectionState, string> = {
  connecting: 'server.wsReconnecting',
  open: 'server.wsOpen',
  reconnecting: 'server.wsReconnecting',
  closed: 'server.wsClosed',
}

const TONE: Record<ConnectionState, 'ok' | 'warn' | 'danger'> = {
  connecting: 'warn',
  open: 'ok',
  reconnecting: 'warn',
  closed: 'danger',
}

export function useConnectionChip(): ConnectionChip {
  const connection = useConnectionState()
  const reconnect = useCallback(() => wsClient.reconnectNow(), [])
  return {
    connection,
    labelKey: LABEL_KEY[connection],
    tone: TONE[connection],
    canReconnect: connection === 'closed',
    reconnect,
  }
}
